/**
 * Resolver for the WP-1 scene-items / presets / package mutation surface.
 *
 * Reads the `VITE_GTWIN_SCENE_ITEMS` env flag (default: `"mock"`).
 *
 *   - `mock` -> live in-memory mock from `sceneItemsMock.ts` (the data path
 *      for tests + offline dev).
 *   - `real` -> WP-14 real adapter from `sceneItemsReal.ts`, which routes
 *      through the FastAPI endpoints documented in
 *      `app-platform-gtwin/docs/proposals/SCENE_ITEMS_SDK_SPEC.md`.
 *
 * Tests can pass an explicit `mode` (without transport options) to
 * exercise the mock; passing `mode: "real"` without transport options
 * returns a "needs-configuration" surface that throws
 * `NotImplementedYetError` on every call, so test imports that never
 * touch a network path stay safe.
 *
 * The real surface needs transport options (`baseUrl`, `getToken`,
 * `fetchImpl`, `defaultTenantId`). The viewer's `GTwinClient` passes
 * them through automatically (see `client.ts`); standalone tests can
 * also construct a real surface via `createRealSceneItemsSurface(...)`
 * directly from `sceneItemsReal.ts`.
 */

import type {
  PackageAdapter,
  SceneItemsAdapter,
  ScenePresetsAdapter,
} from './scene-items.js';
import { sceneItemsMock } from './sceneItemsMock.js';
import {
  createRealSceneItemsSurface,
  type RealSceneItemsAdapterOptions,
} from './sceneItemsReal.js';

export type SceneItemsMode = 'mock' | 'real';

export interface SceneItemsSurface {
  mode: SceneItemsMode;
  items: SceneItemsAdapter;
  presets: ScenePresetsAdapter;
  package: PackageAdapter;
}

export class NotImplementedYetError extends Error {
  constructor(method: string) {
    super(
      `${method} is not configured: the real scene-items surface was ` +
        `requested via VITE_GTWIN_SCENE_ITEMS=real but no transport ` +
        `options (baseUrl / getToken / fetchImpl) were passed. The ` +
        `GTwinClient singleton wires this automatically; if you see ` +
        `this from a test, either pass options or set ` +
        `VITE_GTWIN_SCENE_ITEMS=mock for that test.`,
    );
    this.name = 'NotImplementedYetError';
  }
}

const unconfiguredItems: SceneItemsAdapter = {
  add: () => Promise.reject(new NotImplementedYetError('scene.items.add')),
  remove: () => Promise.reject(new NotImplementedYetError('scene.items.remove')),
  patch: () => Promise.reject(new NotImplementedYetError('scene.items.patch')),
  reorder: () => Promise.reject(new NotImplementedYetError('scene.items.reorder')),
  copyFrom: () => Promise.reject(new NotImplementedYetError('scene.items.copyFrom')),
};

const unconfiguredPresets: ScenePresetsAdapter = {
  activate: () => Promise.reject(new NotImplementedYetError('scene.presets.activate')),
  upsert: () => Promise.reject(new NotImplementedYetError('scene.presets.upsert')),
  remove: () => Promise.reject(new NotImplementedYetError('scene.presets.remove')),
};

const unconfiguredPackage: PackageAdapter = {
  exportGtwin: () => Promise.reject(new NotImplementedYetError('package.exportGtwin')),
};

function readModeFromEnv(): SceneItemsMode {
  if (typeof import.meta === 'undefined') return 'mock';
  const raw = (import.meta as { env?: Record<string, string | undefined> }).env
    ?.VITE_GTWIN_SCENE_ITEMS;
  return raw === 'real' ? 'real' : 'mock';
}

/**
 * Resolve the scene-items surface for the given mode.
 *
 *   - `mode === "mock"` (or unset): always returns the in-memory mock.
 *   - `mode === "real"` + `realOptions` supplied: wires the WP-14 real
 *     adapter. The viewer's `GTwinClient` calls this with the transport
 *     options derived from `GTwinClientOptions`.
 *   - `mode === "real"` + `realOptions` missing: returns a placeholder
 *     surface that throws on every call (helps tests that import the
 *     resolver without intending to hit a network).
 */
export function getSceneItemsAdapter(
  mode?: SceneItemsMode,
  realOptions?: RealSceneItemsAdapterOptions,
): SceneItemsSurface {
  const resolved: SceneItemsMode = mode ?? readModeFromEnv();
  if (resolved === 'real') {
    if (realOptions) {
      const real = createRealSceneItemsSurface(realOptions);
      return {
        mode: 'real',
        items: real.items,
        presets: real.presets,
        package: real.package,
      };
    }
    return {
      mode: 'real',
      items: unconfiguredItems,
      presets: unconfiguredPresets,
      package: unconfiguredPackage,
    };
  }
  return {
    mode: 'mock',
    items: sceneItemsMock.items,
    presets: sceneItemsMock.presets,
    package: sceneItemsMock.package,
  };
}
